import React from 'react';
import Header from './Header.js';
import './PrivacyPolicy.css';

const PRIVACY_POLICY_TEXT = `# Privacy Policy for Shaker: Cocktail Manager

**Last Updated: December 14, 2025**

## Overview

Shaker: Cocktail Manager ("Shaker," "we," "our," or "the app") is committed to protecting your privacy. This policy explains how we handle your information.

## Data Collection and Storage

### What We Collect

Shaker stores the following information **locally on your device only**:

- Cocktail recipes you add (manually, from websites, or via photo scanning)
- Your bar inventory (spirits, liqueurs, and other ingredients)
- Ingredient brands and product details you specify
- App preferences and settings

### Where Your Data Is Stored

All data is stored exclusively in a local SQLite database on your device. **We do not transmit, sync, or upload any of your personal data to external servers.**

## What We Don't Do

- **No Cloud Storage**: Your recipes and bar inventory never leave your device
- **No Account Required**: Shaker works entirely offline without any user accounts
- **No Analytics**: We do not collect usage analytics or telemetry data
- **No Tracking**: We do not track your behavior or activities
- **No Third-Party Sharing**: We have no third parties to share data with
- **No Advertising**: Shaker contains no advertisements or ad networks

## OCR and Image Processing

When you use the photo scanning feature to add recipes:

- Image processing occurs entirely on your device using Apple's Vision Framework
- Photos are processed locally and are not stored permanently
- No images are uploaded to external services
- Extracted text is saved only if you choose to save the recipe

## Data Control

Since all data is stored locally on your device:

- **Your Control**: You have complete control over your data at all times
- **Deletion**: Deleting the app removes all associated data from your device
- **Backup**: Your data is included in standard iOS device backups (if you have iCloud or iTunes backup enabled)
- **No Recovery**: We cannot recover your data if you delete the app or lose your device, as we never have access to it

## Changes to This Policy

We may update this privacy policy as the app evolves. Any changes will be reflected in the "Last Updated" date above and communicated through app updates.

## Contact

If you have questions about this privacy policy or Shaker's data practices, you can contact us at:

benmsheppard@gmail.com

## Your Consent

By using Shaker, you consent to this privacy policy.

---

**Summary**: Shaker stores all your data locally on your device. We never collect, transmit, or share your personal information. Your cocktail recipes and bar inventory are yours alone.`;

class PrivacyPolicy extends React.Component {

  formatContent(text) {
    if (!text) return null;
    
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inList = false;
    let listItems = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle headers
      if (trimmedLine.startsWith('# ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        elements.push(<h1 key={`h1-${index}`}>{trimmedLine.substring(2)}</h1>);
      } else if (trimmedLine.startsWith('## ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(<h2 key={`h2-${index}`}>{trimmedLine.substring(3)}</h2>);
      } else if (trimmedLine.startsWith('### ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(<h3 key={`h3-${index}`}>{trimmedLine.substring(4)}</h3>);
      } 
      // Handle list items
      else if (trimmedLine.startsWith('- ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        inList = true;
        const listText = trimmedLine.substring(2);
        // Handle bold text in list items
        const formattedText = listText.split('**').map((part, i) => 
          i % 2 === 1 ? <strong key={`bold-${index}-${i}`}>{part}</strong> : part
        );
        listItems.push(<li key={`li-${index}`}>{formattedText}</li>);
      }
      // Handle bold text in paragraphs
      else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const boldText = trimmedLine.substring(2, trimmedLine.length - 2);
        elements.push(<p key={`p-${index}`}><strong>{boldText}</strong></p>);
      }
      // Handle empty lines
      else if (trimmedLine === '') {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
      }
      // Handle horizontal rule
      else if (trimmedLine === '---') {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`}>{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(<hr key={`hr-${index}`} />);
      }
      // Regular paragraph text
      else {
        if (inList) {
          elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        // Handle bold text within paragraphs
        const parts = trimmedLine.split('**');
        if (parts.length > 1) {
          const formattedParts = parts.map((part, i) => 
            i % 2 === 1 ? <strong key={`bold-${index}-${i}`}>{part}</strong> : part
          );
          currentParagraph.push(...formattedParts);
        } else {
          currentParagraph.push(trimmedLine);
        }
      }
    });

    // Add any remaining content
    if (currentParagraph.length > 0) {
      elements.push(<p key="p-final">{currentParagraph}</p>);
    }
    if (inList && listItems.length > 0) {
      elements.push(<ul key="ul-final">{listItems}</ul>);
    }

    return elements;
  }

  render() {
    return (
      <div className="PrivacyPolicy-Wrapper">
        <Header selected=""/>
        <div className="PrivacyPolicy-Content">
          {this.formatContent(PRIVACY_POLICY_TEXT)}
        </div>
      </div>
    );
  }
}

export default PrivacyPolicy;

